import { ErrorState, FinancialFigure, formatMoney, KpiCard, KpiGrid, ProvenanceBreakdown, ScreenLayout, SeverityBadge, SkeletonCards } from "@corefinops/ui";
import { navigate } from "../router.js";
import { commandCenterNotices } from "./command-center-state.js";
import type { ReactNode } from "react";
import type { CommandCenterResponse, SerializedMoney } from "../api/client.js";
import { useCommandCenter } from "../api/hooks.js";
import { useSession } from "../api/session.js";
import { PlanBanner } from "./PlanBanner.js";

/**
 * Financial Command Center (UX-02) — the first screen. Bound to the real
 * /command-center API; every number is a persisted measure. Money is shown PER
 * CURRENCY (never summed across currencies), verified value is split by
 * provenance (human-attested vs system-measured), and overlapping savings are
 * counted once. The section order follows the signed-in persona.
 */
export function OverviewScreen(): ReactNode {
  const { session } = useSession();
  const q = useCommandCenter();

  return (
    <ScreenLayout
      title="Financial Command Center"
      scope={`${session.tenantName ?? `Workspace ${session.tenantId.slice(0, 8)}…`} · what you spend, what you can save, what you have proven`}
    >
      {q.isLoading ? (
        <SkeletonCards count={4} label="Loading your command center" />
      ) : q.isError || !q.data ? (
        <ErrorState message={(q.error as Error | null)?.message ?? "Could not load the command center."} onRetry={() => q.refetch()} />
      ) : (
        <CommandCenter data={q.data} />
      )}
    </ScreenLayout>
  );
}

const PERSONA_HEADLINE: Record<CommandCenterResponse["persona"], string> = {
  executive: "Spend, proven value and risk at a glance.",
  finance: "Allocated spend and verified, evidence-backed value.",
  finops: "Your highest-value optimization opportunities, ranked.",
  migration: "Migration economics and the value it is delivering.",
  admin: "Finish setting up your workspace to unlock every insight.",
  viewer: "A read-only view of spend, savings and value.",
};

type Section = "kpis" | "opportunities" | "setup" | "alerts";

const ORDER: Record<CommandCenterResponse["persona"], Section[]> = {
  executive: ["kpis", "opportunities", "alerts", "setup"],
  finance: ["kpis", "alerts", "opportunities", "setup"],
  finops: ["opportunities", "kpis", "alerts", "setup"],
  migration: ["kpis", "opportunities", "alerts", "setup"],
  admin: ["setup", "kpis", "opportunities", "alerts"],
  viewer: ["kpis", "opportunities", "alerts", "setup"],
};

function CommandCenter({ data }: { data: CommandCenterResponse }): ReactNode {
  const setupDone = data.activation.completed >= data.activation.total;
  // A finished checklist moves to the end (it is history, not a call to action).
  const order = ORDER[data.persona].filter((s) => !(s === "setup" && setupDone)).concat(setupDone ? ["setup"] : []);
  const render: Record<Section, ReactNode> = {
    kpis: (
      <div key="kpis" className="cfo-stack">
        <Kpis data={data} />
        <DataBasis data={data} />
      </div>
    ),
    opportunities: <Opportunities key="opportunities" data={data} />,
    setup: <SetupChecklist key="setup" data={data} />,
    alerts: <Alerts key="alerts" data={data} />,
  };
  return (
    <div className="cfo-stack">
      <PlanBanner entitlements={data.entitlements} />
      <p className="cfo-lede">{PERSONA_HEADLINE[data.persona]}</p>
      <Notices data={data} />
      {order.map((s) => render[s])}
      <ExpertCta />
    </div>
  );
}

/** What is missing or different about this workspace right now, and the next step (B7). */
function Notices({ data }: { data: CommandCenterResponse }): ReactNode {
  const notices = commandCenterNotices(data);
  if (notices.length === 0) return null;
  return (
    <section aria-label="Workspace status" className="cfo-stack">
      {notices.map((n) => (
        <div key={n.id} className={`cfo-banner cfo-banner-${n.tone}`} role={n.tone === "danger" ? "alert" : "status"} data-notice={n.id}>
          <span>
            <strong>{n.title}.</strong> {n.body}
          </span>
          {n.action ? (
            <button type="button" className="cfo-btn-ghost" onClick={() => navigate(n.action!.path)}>
              {n.action.label}
            </button>
          ) : null}
        </div>
      ))}
    </section>
  );
}

const money = (list: readonly SerializedMoney[]): string => (list.length ? list.map((m) => formatMoney(m)).join(" · ") : "—");

/** What the figures are based on, from the ingestion pipeline: measure, period, freshness, coverage, gaps. */
function DataBasis({ data }: { data: CommandCenterResponse }): ReactNode {
  const q = data.dataQuality;
  return (
    <section className="cfo-card" aria-label="Data basis and coverage">
      <h2 className="cfo-card-head">What these figures are based on</h2>
      <p className="cfo-card-sub" style={{ margin: "2px 0 8px" }}>
        {q.spendBasis.label}. {q.period ? <>Month shown: <strong>{q.period.slice(0, 7)}</strong>{q.dataThrough ? <>, data through <strong>{q.dataThrough}</strong></> : null}.</> : "No cost data has been imported yet."}
        {q.lastIngestionAt ? <> Last refreshed {q.lastIngestionAt.slice(0, 10)}{q.stale ? " (out of date)" : ""}.</> : null}
      </p>
      <FinancialFigure
        label="Spend shown on this page"
        amounts={data.overview.allocatedByCurrency}
        basis={q.basis}
        asOf={q.dataThrough}
        coverage={q.coverage.dayCoverage}
        per="so far this month"
      />
      <p className="cfo-muted" style={{ margin: "8px 0 0", fontSize: "0.8rem" }}>
        {q.coverage.accountsWithCost} of {q.coverage.connectedAccounts} connected account{q.coverage.connectedAccounts === 1 ? "" : "s"} have cost data · {q.coverage.daysWithData} of {q.coverage.daysExpected} expected days.
        Savings opportunities are <strong>projected</strong>; verified value is <strong>observed</strong> and proven.
      </p>
      {q.missingSources.length ? (
        <ul className="cfo-checklist" aria-label="Missing data sources" style={{ marginTop: 8 }}>
          {q.missingSources.map((m, i) => (
            <li key={`${m.kind}-${i}`}>
              <span className="cfo-check" aria-hidden="true">!</span>
              <span>{m.message}</span>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function Kpis({ data }: { data: CommandCenterResponse }): ReactNode {
  const o = data.overview;
  const allocated = o.allocatedByCurrency.length ? o.allocatedByCurrency : [];
  return (
    <section aria-label="Key figures">
      <KpiGrid>
        {allocated.length === 0 ? (
          <KpiCard label="Allocated spend / mo" value="—" measure="net_amortized_cost" sub="Connect AWS and ingest cost to populate" />
        ) : (
          allocated.map((m) => (
            <KpiCard key={m.currency} label={`Allocated spend / mo${allocated.length > 1 ? ` (${m.currency})` : ""}`} value={formatMoney(m)} measure="net_amortized_cost" confidence="Imported" />
          ))
        )}
        <KpiCard label="Unallocated spend / mo" value={money(o.unallocatedByCurrency)} measure="net_amortized_cost" sub="Conservatively left unattributed" />
        <KpiCard
          label="Verified value / mo"
          value={money(o.verifiedSavingsByCurrency)}
          confidence="Verified"
          sub={
            <>
              <ProvenanceBreakdown attested={o.verifiedSavingsByProvenance.customerAttested} measured={o.verifiedSavingsByProvenance.systemMeasured} unverified={o.verifiedSavingsByProvenance.unverified} />
              {o.overlapSuppressedEntries > 0 ? <> {o.overlapSuppressedEntries} overlapping entr{o.overlapSuppressedEntries === 1 ? "y" : "ies"} counted once</> : null}
            </>
          }
        />
        <KpiCard label="Open opportunities" value={data.opportunities.total} sub={`Potential ${money(data.opportunities.potentialByCurrency)} / mo`} />
        <KpiCard label="AWS accounts" value={o.awsAccounts} />
        <KpiCard label="Applications · workloads" value={`${o.applications} · ${o.workloads}`} />
        <KpiCard label="Open findings" value={o.openFindings} />
        <KpiCard label="Open actions" value={o.openActions} />
      </KpiGrid>
      {o.multipleCurrencies ? <p className="cfo-note" style={{ marginTop: 12 }}>Amounts are shown per currency and are never added across currencies.</p> : null}
    </section>
  );
}

function Opportunities({ data }: { data: CommandCenterResponse }): ReactNode {
  const top = data.topOpportunities;
  return (
    <section className="cfo-card" aria-label="Top savings opportunities">
      <div className="cfo-card-row">
        <div>
          <h2 className="cfo-card-head">Top savings opportunities</h2>
          <div className="cfo-card-sub">
            Non-overlapping potential {money(data.opportunities.potentialByCurrency)} / mo
            {data.opportunities.suppressedByCurrency.length ? ` · ${money(data.opportunities.suppressedByCurrency)} excluded as overlapping` : ""}
          </div>
        </div>
        <button type="button" className="cfo-btn-ghost" onClick={() => navigate("/radar")}>Open Savings Radar</button>
      </div>
      {top.length === 0 ? (
        <p className="cfo-muted">No open opportunities yet. They appear after AWS recommendations are refreshed and findings are evaluated.</p>
      ) : (
        <ol className="cfo-rank-list">
          {top.map((r) => (
            <li key={r.recommendationId}>
              <span className="cfo-rank">{r.rank}</span>
              <span className="cfo-rank-title">
                {r.title}
                {r.evidence.observedCondition ? <span className="cfo-muted"> — {r.evidence.observedCondition}</span> : null}
              </span>
              <span className="cfo-rank-value">{r.expectedSavings ? `${formatMoney(r.expectedSavings)} / mo` : "—"}</span>
            </li>
          ))}
        </ol>
      )}
      {data.opportunities.lockedCount > 0 ? (
        <p className="cfo-note">
          {data.opportunities.lockedCount} more opportunit{data.opportunities.lockedCount === 1 ? "y is" : "ies are"} available on paid plans.{" "}
          <a href="/settings/billing" onClick={(e) => { e.preventDefault(); navigate("/settings/billing"); }}>See plans</a>
        </p>
      ) : null}
    </section>
  );
}

const STEPS: Record<string, { label: string; link: string; hint: string }> = {
  workspace_created: { label: "Create your workspace", link: "/settings", hint: "Done when you signed up." },
  aws_connected: { label: "Connect an AWS account", link: "/settings", hint: "Settings → AWS connections. Read-only role; no credentials stored." },
  first_cost_ingested: { label: "Import cost data", link: "/settings", hint: "Requires a CUR 2.0 Data Export. Runs daily once connected." },
  first_recommendation: { label: "Get your first recommendations", link: "/settings", hint: "AWS-native recommendations refresh daily." },
  first_action: { label: "Act on an opportunity", link: "/radar", hint: "Turn a recommendation into a tracked action." },
  first_verified_saving: { label: "Verify a saving", link: "/actions", hint: "Record evidence so value is proven, not estimated." },
  subscribed: { label: "Choose a plan", link: "/settings/billing", hint: "Card billing or AWS Marketplace." },
};

function SetupChecklist({ data }: { data: CommandCenterResponse }): ReactNode {
  const a = data.activation;
  const f = a.freshness;
  return (
    <section className="cfo-card" aria-label="Setup progress">
      <h2 className="cfo-card-head">Getting to first value · {a.completed}/{a.total}</h2>
      <div className="cfo-card-sub">
        {f.connectedAccounts} connected account{f.connectedAccounts === 1 ? "" : "s"}
        {f.incompleteConnections ? ` · ${f.incompleteConnections} connection${f.incompleteConnections === 1 ? "" : "s"} not finished — resume in Settings → AWS connections` : ""}
        {f.lastCostIngestionAt ? ` · cost data as of ${f.lastCostIngestionAt.slice(0, 10)}` : ""}
        {f.stale ? " · data is out of date" : ""}
        {f.failedJobsLast24h ? ` · ${f.failedJobsLast24h} failed job${f.failedJobsLast24h === 1 ? "" : "s"} in 24h` : ""}
      </div>
      <ul className="cfo-checklist">
        {a.milestones.map((m) => {
          const step = STEPS[m.milestone];
          if (!step) return null;
          const done = Boolean(m.reachedAt);
          return (
            <li key={m.milestone} className={done ? "done" : undefined}>
              <span className="cfo-check" aria-hidden="true">{done ? "✓" : ""}</span>
              <span>
                {done ? (
                  step.label
                ) : (
                  <a href={step.link} onClick={(e) => { e.preventDefault(); navigate(step.link); }}>{step.label}</a>
                )}
                <span className="cfo-muted"> — {done ? `done ${m.reachedAt!.slice(0, 10)}` : step.hint}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function Alerts({ data }: { data: CommandCenterResponse }): ReactNode {
  return (
    <section className="cfo-card" aria-label="Recent alerts">
      <h2 className="cfo-card-head">Alerts{data.unreadAlerts ? ` · ${data.unreadAlerts} unread` : ""}</h2>
      {data.alerts.length === 0 ? (
        <p className="cfo-muted">No alerts. You will be told here (and by email on paid plans) about new high-severity findings, stale data and failed syncs.</p>
      ) : (
        <ul className="cfo-alert-list">
          {data.alerts.map((n) => (
            <li key={n.notificationId} className={n.readAt ? undefined : "unread"}>
              <SeverityBadge severity={n.severity} />
              <span>
                {n.link ? <a href={n.link} onClick={(e) => { e.preventDefault(); navigate(n.link!); }}>{n.title}</a> : n.title}
                {n.body ? <span className="cfo-muted"> — {n.body}</span> : null}
              </span>
              <span className="cfo-muted">{n.createdAt.slice(0, 10)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ExpertCta(): ReactNode {
  return (
    <section className="cfo-note cfo-card-row">
      <span>
        <strong>Want a second pair of eyes?</strong> Cloudain FinOps experts can review your opportunities or plan commitments — only with your consent.
      </span>
      <button type="button" className="cfo-btn-ghost" onClick={() => navigate("/help")}>Request expert help</button>
    </section>
  );
}
